import {
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CustomButton from "components/atoms/CustomButton";
import React, { useMemo } from "react";

import CustomTextInput from "components/atoms/CustomTextInput";
import { v4 as uuidv4 } from "uuid";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import CustomModal from "components/atoms/CustomModal";
import CancelIcon from "@mui/icons-material/Cancel";
import { red } from "@mui/material/colors";
import InputDataStockTable from "components/molecules/tabels/radio/InputDataStockTable";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { GET_RADIOLOGY_TEST_INFO, GET_TEST_INFO } from "utils/reactQueryKeys";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import FileUploadButton from "components/atoms/FileUploadButton";
import CustomSelect from "components/atoms/Select";
import { type } from "@testing-library/user-event/dist/type";
import ColorCodeChip from "components/atoms/ColorCodeChip";
import FileUploadBoard from "components/atoms/FileUploadBoard";
import FileCard from "components/atoms/FileCard";

const Details = ({ title, desc }) => {
  return (
    <Stack
      direction={"column"}
      spacing={1}
      alignItems={"flex-start"}
      justifyContent="center"
    >
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {title}
      </Typography>
      <Typography textTransform={"capitalize"} variant="body1">
        {desc}
      </Typography>
    </Stack>
  );
};
const formatApiData = (testResultFields, otherFields) => {
  let testResultObj = {};

  testResultFields.forEach((result) => {
    testResultObj[result.name] = {
      value: result.value,
    };
  });

  return {
    dynamicFields: testResultObj,
    resultSummary: otherFields.comment,
  };
};
const formatCompleteInvestigationData = (stockUsed) => {
  const formatStockUsed = stockUsed.map((stock) => ({
    item: stock.item,
    quantity: +stock.quantity,
  }));
  return {
    stockUsed: formatStockUsed,
  };
};
function InputDataModalView({ handleClose, modalData }) {
  const modalRef = React.useRef(null);
  const queryClient = useQueryClient();
  const [modalFormsState, setmodalFormsState] = React.useState({
    name: "",
  });
  const [testResultFields, settestResultFields] = React.useState([]);
  const [otherFields, setotherFields] = React.useState({
    comment: "",
    files: [],
  });

  React.useMemo(() => {
    if (modalData?.test?.otherFields)
      settestResultFields(modalData?.test?.otherFields);
  }, [modalData?.test?.otherFields]);
  const [stockUsed, setstockUsed] = React.useState([]);

  //complete investigation data
  const { mutateAsync: completeTest, isLoading: completeTestLoading } =
    useCustomMutation(
      {
        url: `/radiology/complete-investigation/${modalData._id}`,
        method: "patch",
        data: {
          ...formatCompleteInvestigationData(stockUsed),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(GET_RADIOLOGY_TEST_INFO);
          toast.success("Success");
          handleClose();
        },

        onError: (error) => {
          toast.error(error.message);
        },
      }
    );

  //fill investigation report
  const {
    mutateAsync: fillInvestigationResult,
    isLoading: fillInvestigationResultLoading,
  } = useCustomMutation({
    url: `/investigation/fill-investigation-result/${modalData._id}`,
    method: "patch",
    data: formatApiData(testResultFields, otherFields),
  });
  //upload investigation report
  const { mutateAsync: uploadFiles, isLoading: uploadFilesLoading } =
    useCustomMutation({
      url: `/investigation/upload-investigation-result-files/${modalData._id}`,
      method: "patch",
    });

  const submitReport = async () => {
    try {
      const promises = [fillInvestigationResult()];

      if (otherFields.files.length) {
        const formData = new FormData();
        otherFields.files.forEach((file, i) =>
          formData.append(`resultFiles_${i + 1}`, file)
        );
        promises.unshift(uploadFiles(formData));
      }
      if (stockUsed.length) promises.unshift(completeTest());
      await Promise.all(promises);

      queryClient.invalidateQueries(GET_RADIOLOGY_TEST_INFO);
      toast.success("Success");
      handleClose();
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong, please refresh and try again later");
    }
  };

  const handleChange = (i, e) => {
    settestResultFields((prev) => {
      const copy = [...prev];
      const field = copy[i];
      const fieldWithvalue = {
        ...field,
        value: e.target.value,
      };
      copy[i] = fieldWithvalue;
      return copy;
    });
  };
  const handleChangeStockDetail = (i, e) => {
    setstockUsed((prev) => {
      const copy = [...prev];
      const field = copy[i];
      const fieldWithvalue = {
        ...field,
        [e.target.name]: e.target.value,
      };
      copy[i] = fieldWithvalue;
      return copy;
    });
  };
  const handleDeleteStock = (uid) => {
    setstockUsed(() => {
      const data = stockUsed.filter((result) => result.uid !== uid);
      return data;
    });
  };
  const handleDeleteTestResultField = (id) => {
    settestResultFields(() => {
      const data = testResultFields.filter((result) => result.id !== id);
      return data;
    });
  };

  const openModal = () => {
    modalRef?.current?.handleToggle();
  };

  const handleAddNewField = () => {
    if (!modalFormsState.name) return toast.error("Please add a name");

    const newRow = {
      id: uuidv4(),
      value: "",
      addedFromInputDataModal: true,
      ...modalFormsState,
    };
    settestResultFields((prev) => {
      return [...prev, newRow];
    });
    setmodalFormsState({
      name: "",
    });
    openModal();
  };
  const handleAddStock = () => {
    const newRow = {
      uid: uuidv4(),
      item: "",
      // unit: "",
      quantity: "",
    };
    setstockUsed((prev) => {
      return [...prev, newRow];
    });
  };

  const onAddFiles = (files) => {
    setotherFields((prev) => {
      return {
        ...prev,
        files: [...prev.files, ...Array.from(files)],
      };
    });
  };
  const onDeleteFile = (file) => {
    setotherFields((prev) => {
      const data = prev.files.filter((_file) => _file.name !== file.name);
      return {
        ...prev,
        files: data,
      };
    });
  };

  const handleModalFormChange = (e) => {
    setmodalFormsState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };
  return (
    <>
      <Stack
        direction={"column"}
        spacing={3}
        sx={{ width: "100%", mt: 3, p: 3 }}
      >
        <Stack direction={"column"} spacing={1}>
          <Typography variant="displayMd">Input Data</Typography>
          <Typography>{modalData?.test?.testName}</Typography>
        </Stack>

        <Divider />
        <Stack
          direction={"row"}
          spacing={2}
          alignItems={"center"}
          justifyContent="space-between"
          sx={{
            p: 2,
            borderRadius: "10px",
            backgroundColor: "background.gray3",

            width: "100%",
            border: "1px dashed rgba(0,0,0,0.5)",
          }}
        >
          <Stack direction={"row"} spacing={2}>
            <Details
              title="PATIENT NAME"
              desc={`${modalData?.patient?.firstName} ${modalData?.patient?.lastName}`}
            />
            <Details
              title="AGE/Gender"
              desc={`${modalData?.patient?.age}Yrs/${modalData?.patient?.gender}`}
            />
            <Details title="Unique id" desc={modalData?.patient?.ID} />
          </Stack>
        </Stack>
        <Stack
          direction={"row"}
          width="100%"
          justifyContent={"flex-end"}
        ></Stack>
        <Grid container spacing={3} sx={{ mt: 1, width: "100%" }}>
          {testResultFields.map((field, i) => (
            <Grid key={field.id} item xs={12} sm={6}>
              <Stack>
                <Stack
                  direction={"row"}
                  alignItems="center"
                  justifyContent={"center"}
                  width="100%"
                  gap={1}
                >
                  <CustomTextInput
                    title={field.name}
                    value={field?.value || ""}
                    name={field.name}
                    handleChange={handleChange.bind(null, i)}
                    placeholder="Input value"
                    boxSx={{ width: "100% !important" }}
                  />
                  {field?.addedFromInputDataModal && (
                    <IconButton
                      onClick={handleDeleteTestResultField.bind(null, field.id)}
                      size="small"
                      sx={{ mt: "20px !important" }}
                    >
                      <CancelIcon sx={{ color: red[500], fontSize: "17px" }} />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Stack spacing={4} width={"100%"}>
          <Stack direction={"row"} spacing={3} alignItems={"center"}>
            <svg
              width="40"
              height="41"
              viewBox="0 0 40 41"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="20"
                cy="20.6094"
                r="19"
                fill="white"
                stroke="#CBD0DC"
                strokeWidth="2"
              />
              <path
                d="M16.2982 26.5947H14.7322C12.3344 26.4235 11.2578 24.5802 11.2578 22.9408C11.2578 21.3015 12.3344 19.4501 14.6915 19.2869C15.0259 19.2543 15.3195 19.5153 15.3439 19.8579C15.3684 20.1923 15.1156 20.4859 14.7731 20.5103C13.1908 20.6245 12.4812 21.8153 12.4812 22.949C12.4812 24.0827 13.1908 25.2735 14.7731 25.3876H16.2982C16.6326 25.3876 16.9099 25.6649 16.9099 25.9993C16.9099 26.3337 16.6326 26.5947 16.2982 26.5947Z"
                fill="#292D32"
              />
              <path
                d="M23.8082 26.5954C23.7918 26.5954 23.7837 26.5954 23.7674 26.5954C23.433 26.5954 23.1231 26.3181 23.1231 25.9837C23.1231 25.633 23.3841 25.372 23.7266 25.372C24.7298 25.372 25.627 25.0213 26.3284 24.3933C27.6007 23.2841 27.6823 21.6855 27.3397 20.56C26.9972 19.4426 26.0429 18.1621 24.3791 17.9582C24.1099 17.9256 23.8979 17.7217 23.8489 17.4525C23.5227 15.4951 22.4706 14.1412 20.872 13.6518C19.2245 13.138 17.2996 13.6437 16.1007 14.8997C14.9344 16.115 14.6652 17.8196 15.3422 19.6955C15.4564 20.0135 15.2933 20.3642 14.9752 20.4784C14.6571 20.5926 14.3064 20.4295 14.1922 20.1114C13.3685 17.8114 13.7436 15.6093 15.2199 14.0596C16.7287 12.4774 19.1511 11.8493 21.2309 12.4855C23.1394 13.0728 24.4851 14.6469 24.9745 16.8327C26.6383 17.2079 27.9759 18.4721 28.506 20.2174C29.0851 22.1178 28.5631 24.0752 27.1358 25.315C26.2305 26.1306 25.0479 26.5954 23.8082 26.5954Z"
                fill="#292D32"
              />
              <path
                d="M20.0003 28.9927C18.3609 28.9927 16.8276 28.12 15.9875 26.709C15.8978 26.5704 15.8081 26.4073 15.7347 26.2278C15.4574 25.6487 15.3105 24.9881 15.3105 24.303C15.3105 21.7175 17.4148 19.6133 20.0003 19.6133C22.5857 19.6133 24.69 21.7175 24.69 24.303C24.69 24.9963 24.5432 25.6487 24.2496 26.2523C24.1843 26.4073 24.0946 26.5704 23.9967 26.7253C23.173 28.12 21.6396 28.9927 20.0003 28.9927ZM20.0003 20.8367C18.0918 20.8367 16.534 22.3945 16.534 24.303C16.534 24.8087 16.64 25.2817 16.8439 25.714C16.9091 25.8526 16.9662 25.9668 17.0315 26.0729C17.6513 27.125 18.785 27.7693 19.9921 27.7693C21.1992 27.7693 22.3329 27.125 22.9446 26.0892C23.018 25.9668 23.0833 25.8526 23.1322 25.7385C23.3524 25.2899 23.4584 24.8168 23.4584 24.3111C23.4666 22.3945 21.9088 20.8367 20.0003 20.8367Z"
                fill="#292D32"
              />
              <path
                d="M19.5343 25.7221C19.3793 25.7221 19.2244 25.665 19.1021 25.5427L18.2946 24.7353C18.0581 24.4987 18.0581 24.1072 18.2946 23.8707C18.5311 23.6342 18.9226 23.6342 19.1591 23.8707L19.5506 24.2622L20.8556 23.0551C21.1084 22.8267 21.4918 22.8431 21.7201 23.0877C21.9485 23.3324 21.9322 23.7239 21.6875 23.9523L19.9503 25.559C19.8279 25.665 19.6811 25.7221 19.5343 25.7221Z"
                fill="#292D32"
              />
            </svg>

            <hgroup>
              <Typography
                variant="h6"
                sx={{ fontWeight: "800", fontSize: "24px" }}
              >
                Import record
              </Typography>
              <Typography
                sx={{ fontWeight: "400", fontSize: "14px", opacity: 0.7 }}
              >
                Select and upload the patient medical records
              </Typography>
            </hgroup>
          </Stack>
          <Box width={{ xs: "100%", md: "60%" }}>
            <FileUploadBoard
              onAddFiles={onAddFiles}
              acceptedFiles={["pdf", "image"]}
            />
          </Box>
          <Grid container spacing={2} sx={{ mt: 1, width: "100%" }}>
            {otherFields.files.map((file) => (
              <Grid key={file.name + Date.now()} item xs={12} lg={6}>
                {" "}
                <FileCard
                  file={file}
                  onDeleteFile={onDeleteFile}
                  containerSx={{ width: { xs: "250px", sm: "348px" } }}
                  nameSx={{ width: { xs: "130px", sm: "230px" } }}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>

        <Stack spacing={1.5}>
          <Typography variant="heading">Stock usage</Typography>
          <InputDataStockTable
            data={stockUsed}
            handleChangeDetail={handleChangeStockDetail}
            handleDeleteStock={handleDeleteStock}
            handleAddStock={handleAddStock}
            investigationId={modalData?._id}
          />
        </Stack>

        <CustomTextInput
          title={"Summary/Comment"}
          value={otherFields?.comment || ""}
          name={"comment"}
          handleChange={(e) =>
            setotherFields((prev) => ({ ...prev, comment: e.target.value }))
          }
          placeholder="Text/Number goes in here"
          multiline
          rows={4}
        />

        <Stack spacing={2} width="20%">
          <CustomButton
            text={"Save"}
            color="secondary"
            onClick={submitReport}
            disabled={
              completeTestLoading ||
              fillInvestigationResultLoading ||
              uploadFilesLoading
            }
          />
        </Stack>
      </Stack>
      <CustomModal
        ref={modalRef}
        childrenContSx={{
          p: 3,
          pt: 5,
          height: "fit-content !important",
          minWidth: "30vw !important",
        }}
      >
        <Stack spacing={2}>
          <CustomTextInput
            title="Field Name"
            value={modalFormsState.name}
            name={"name"}
            handleChange={handleModalFormChange}
            boxSx={{ width: "100%" }}
            placeholder="Input field name"
          />

          <Box>
            <CustomButton
              text={"Add"}
              onClick={handleAddNewField}
              variant="contained"
              color="secondary"
            />
          </Box>
        </Stack>
      </CustomModal>
    </>
  );
}

export default InputDataModalView;
